import { useState, useMemo, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import { usePlanPermissions } from '@/hooks/usePlanPermissions';
import { useUpgradeModal } from '@/context/UpgradeModalContext';
import { SwalAlert } from '@/hooks/useSweetAlert';
import { LoadingSkeleton } from '@/components/base/LoadingSkeleton';
import { ErrorState } from '@/components/base/ErrorState';
import {
  storageFiles,
  fileTypeLabels,
  fileTypeIcons,
  fileTypeColors,
  getTotalStorageUsed,
  formatStorageGB,
  type FileType,
  type StorageFile,
} from '@/mocks/adminStorage';

const allTypes: FileType[] = ['fotos', 'videos', 'audios', 'lives'];

export default function AdminStorage() {
  const { planId, isFree, isPro, isPremium, storageTotalGB } = usePlanPermissions();
  const { openUpgradeModal } = useUpgradeModal();

  const [files, setFiles] = useState<StorageFile[]>(storageFiles);
  const [filterType, setFilterType] = useState<FileType | 'todos'>('todos');
  const [detailFile, setDetailFile] = useState<StorageFile | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);

  const [pageState, setPageState] = useState<'loading' | 'ready' | 'error'>('loading');

  const loadData = useCallback(() => {
    setPageState('loading');
    setTimeout(() => {
      const shouldError = Math.random() < 0.08;
      setPageState(shouldError ? 'error' : 'ready');
    }, 600);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Compute storage used from current files
  const storageUsedMB = useMemo(() => getTotalStorageUsed(files), [files]);
  const storageUsedGB = useMemo(() => parseFloat(formatStorageGB(storageUsedMB)), [storageUsedMB]);
  const storagePercentage = useMemo(() => Math.min(Math.round((storageUsedMB / (storageTotalGB * 1024)) * 100), 100), [storageUsedMB, storageTotalGB]);

  // Filtered files
  const filteredFiles = useMemo(() => {
    if (filterType === 'todos') return files;
    return files.filter((f) => f.type === filterType);
  }, [files, filterType]);

  // Stats by type
  const statsByType = useMemo(() => {
    return allTypes.map((type) => {
      const typeFiles = files.filter((f) => f.type === type);
      const totalMB = typeFiles.reduce((s, f) => s + f.sizeMB, 0);
      return { type, count: typeFiles.length, totalMB };
    });
  }, [files]);

  // Heavy files blocked on Free
  const heavyFiles = useMemo(() => files.filter((f) => f.isPesado), [files]);

  // Delete file
  const handleDelete = useCallback((file: StorageFile) => {
    SwalAlert.confirm('¿Eliminar archivo?', `Se eliminará permanentemente "${file.name}". Esta acción no se puede deshacer.`).then((result) => {
      if (result.isConfirmed) {
        setFiles((prev) => prev.filter((f) => f.id !== file.id));
        SwalAlert.toast('Archivo eliminado', 'success');
      }
    });
  }, []);

  // View detail
  const handleViewDetail = useCallback((file: StorageFile) => {
    setDetailFile(file);
  }, []);

  // Request more storage
  const handleRequestStorage = useCallback(() => {
    if (isFree) {
      SwalAlert.noConnection(
        'Plan Free — 1 GB máximo',
        'Tu plan actual solo incluye 1 GB para datos ligeros y chat. Actualizá a Pro (50 GB) o Premium (500 GB) para ampliar tu almacenamiento.',
        'Ver planes',
      ).then((result) => {
        if (result.isConfirmed) {
          openUpgradeModal();
        }
      });
    } else if (isPro) {
      SwalAlert.noConnection(
        'Plan Pro — 50 GB',
        'Estás usando el plan Pro con 50 GB de almacenamiento. Para aumentar a 500 GB, actualizá a Premium.',
        'Ver Premium',
      ).then((result) => {
        if (result.isConfirmed) {
          openUpgradeModal();
        }
      });
    } else {
      SwalAlert.success('Plan Premium', 'Tenés 500 GB de almacenamiento. Si necesitás más, contactá a soporte.');
    }
  }, [isFree, isPro, openUpgradeModal]);

  // Export report
  const handleExportReport = useCallback(() => {
    Swal.fire({
      title: 'Generando reporte',
      text: 'Preparando el informe de almacenamiento...',
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });
    setTimeout(() => {
      Swal.close();
      SwalAlert.success('Reporte exportado (mock)', 'El reporte de almacenamiento se generó correctamente. Revisá tu email en unos minutos.');
    }, 1800);
  }, []);

  // Plan badge and color
  const planBadge = useMemo(() => {
    if (isFree) return { label: 'Plan Free', color: 'bg-secondary-100 text-secondary-700 border-secondary-200', icon: 'ri-seedling-line' };
    if (isPro) return { label: 'Plan Pro', color: 'bg-primary-100 text-primary-700 border-primary-200', icon: 'ri-flashlight-line' };
    return { label: 'Plan Premium', color: 'bg-accent-100 text-accent-700 border-accent-200', icon: 'ri-vip-crown-line' };
  }, [isFree, isPro]);

  // Bar color based on usage
  const barColor = useMemo(() => {
    if (storagePercentage >= 90) return 'bg-red-500';
    if (storagePercentage >= 70) return 'bg-amber-500';
    return 'bg-emerald-500';
  }, [storagePercentage]);

  return (
    <div className="min-h-screen bg-background-100">
      {/* Mobile topbar */}
      <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200">
        <div className="flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-accent-500 flex items-center justify-center">
              <i className="ri-hard-drive-2-line text-background-50 text-xs"></i>
            </div>
            <div>
              <h1 className="text-foreground-950 font-bold text-sm">Almacenamiento</h1>
              <p className="text-foreground-400 text-[9px]">{storageUsedGB} GB / {storageTotalGB} GB</p>
            </div>
          </div>
          <Link
            to="/admin/dashboard"
            className="flex items-center gap-1.5 text-foreground-500 text-xs hover:text-foreground-700 transition-colors"
          >
            <i className="ri-arrow-left-line text-xs"></i>
            Volver
          </Link>
        </div>
      </header>

      <div className="flex">


        <main className="flex-1 min-w-0">
          {/* Desktop toolbar */}
          <div className="hidden lg:flex items-center justify-between h-16 px-8 border-b border-background-200 bg-background-50 sticky top-0 z-20">
            <div className="flex items-center gap-4">
              <div>
                <h2 className="text-foreground-950 font-bold text-base">Almacenamiento</h2>
                <p className="text-foreground-400 text-xs">Gestioná el espacio de tu radio</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Link
                to="/admin/dashboard"
                className="px-3 py-1.5 rounded-lg text-[11px] font-semibold text-foreground-400 bg-background-100 hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap"
              >
                <i className="ri-dashboard-line text-xs mr-1"></i>
                Dashboard
              </Link>
            </div>
          </div>

          {/* Content */}
          <div className="p-4 md:p-6 lg:p-8 pb-8 space-y-5">
            <div className="max-w-[1100px] mx-auto space-y-5">
              {pageState === 'loading' && <LoadingSkeleton variant="table" rows={6} cols={4} />}
              {pageState === 'error' && <ErrorState title="Error al cargar almacenamiento" message="No pudimos obtener los datos de almacenamiento. Intentá de nuevo." onRetry={loadData} />}
              {pageState === 'ready' && (
                <>
              {/* Storage Overview Card */}
              <div className="bg-background-50 rounded-2xl border border-background-200 p-5 lg:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-2xl bg-accent-100 flex items-center justify-center shrink-0">
                      <i className="ri-hard-drive-2-line text-accent-600 text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-foreground-950 font-bold text-sm">Uso de Almacenamiento</h3>
                      <p className="text-foreground-400 text-[10px]">{files.length} archivos en total</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-[10px] font-semibold ${planBadge.color}`}>
                      <i className={`${planBadge.icon} text-[10px]`}></i>
                      {planBadge.label}
                    </span>
                    <div className="text-right">
                      <p className="text-lg font-bold text-foreground-950">
                        {storageUsedGB} <span className="text-foreground-400 font-medium text-xs">/ {storageTotalGB} GB</span>
                      </p>
                      <p className={`text-[10px] font-semibold ${storagePercentage >= 90 ? 'text-red-500' : storagePercentage >= 70 ? 'text-amber-500' : 'text-emerald-500'}`}>
                        {storagePercentage}% usado
                      </p>
                    </div>
                  </div>
                </div>

                {/* Progress bar */}
                <div className="relative w-full h-3 rounded-full bg-background-200 overflow-hidden mb-2">
                  <div
                    className={`absolute inset-y-0 left-0 rounded-full transition-all duration-700 ${barColor}`}
                    style={{ width: `${storagePercentage}%` }}
                  ></div>
                </div>

                {/* Markers */}
                <div className="flex items-center justify-between text-[10px] text-foreground-400 mb-2">
                  <span>0 GB</span>
                  <span>{Math.round(storageTotalGB / 2)} GB</span>
                  <span>{storageTotalGB} GB</span>
                </div>

                {/* Free plan warning */}
                {isFree && (
                  <div className="mt-3 p-3 rounded-xl bg-amber-50 border border-amber-200 flex items-start gap-2.5">
                    <i className="ri-alert-line text-amber-600 text-sm mt-0.5"></i>
                    <div>
                      <p className="text-amber-800 text-[10px] font-semibold">Plan Free — 1 GB máximo</p>
                      <p className="text-amber-600/80 text-[9px] mt-0.5">
                        Solo disponible para datos ligeros y chat. Los archivos pesados (videos, lives) no están permitidos. {heavyFiles.length > 0 && `${heavyFiles.length} archivo(s) bloqueado(s).`}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Stats by type */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {statsByType.map((s) => (
                  <button
                    key={s.type}
                    onClick={() => setFilterType(filterType === s.type ? 'todos' : s.type)}
                    className={`bg-background-50 rounded-2xl border p-4 text-left transition-all cursor-pointer ${
                      filterType === s.type
                        ? 'border-accent-500 ring-1 ring-accent-500/20'
                        : 'border-background-200 hover:border-background-300'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1.5">
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${isFree && ['videos', 'lives'].includes(s.type) ? 'bg-red-100' : 'bg-background-200'}`}>
                        <i className={`${fileTypeIcons[s.type]} ${isFree && ['videos', 'lives'].includes(s.type) ? 'text-red-400' : 'text-foreground-500'} text-sm`}></i>
                      </div>
                      <span className="text-foreground-600 text-[10px] font-semibold">{fileTypeLabels[s.type]}</span>
                    </div>
                    <p className="text-xl font-bold text-foreground-950">{s.count}</p>
                    <p className="text-foreground-400 text-[10px] mt-0.5">
                      {(s.totalMB / 1024).toFixed(1)} GB
                      {isFree && ['videos', 'lives'].includes(s.type) && (
                        <span className="text-red-400 ml-1">— bloqueado</span>
                      )}
                    </p>
                  </button>
                ))}
              </div>

              {/* Filtered file list */}
              <div className="bg-background-50 rounded-2xl border border-background-200 overflow-hidden">
                {/* Filters bar */}
                <div className="flex items-center justify-between px-5 py-3 border-b border-background-200">
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => setFilterType('todos')}
                      className={`px-3 py-1.5 rounded-full text-[10px] font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                        filterType === 'todos'
                          ? 'bg-foreground-950 text-background-50'
                          : 'bg-background-100 text-foreground-500 hover:bg-background-200'
                      }`}
                    >
                      Todos ({files.length})
                    </button>
                    {allTypes.map((type) => {
                      const count = files.filter((f) => f.type === type).length;
                      const blocked = isFree && (type === 'videos' || type === 'lives');
                      return (
                        <button
                          key={type}
                          onClick={() => setFilterType(type)}
                          className={`px-3 py-1.5 rounded-full text-[10px] font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                            filterType === type
                              ? 'bg-foreground-950 text-background-50'
                              : blocked
                                ? 'bg-red-50 text-red-400 line-through'
                                : 'bg-background-100 text-foreground-500 hover:bg-background-200'
                          }`}
                        >
                          {fileTypeLabels[type]} ({count})
                        </button>
                      );
                    })}
                  </div>
                  <span className="text-foreground-400 text-[10px]">{filteredFiles.length} resultados</span>
                </div>

                {/* File table */}
                {filteredFiles.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="w-12 h-12 mx-auto rounded-xl bg-background-200 flex items-center justify-center mb-3">
                      <i className="ri-file-search-line text-foreground-400 text-lg"></i>
                    </div>
                    <p className="text-sm font-semibold text-foreground-600">Sin archivos</p>
                    <p className="text-xs text-foreground-400 mt-1">
                      {filterType === 'todos' ? 'No hay archivos en tu almacenamiento.' : `No hay archivos de tipo "${fileTypeLabels[filterType]}".`}
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-background-200">
                          <th className="text-left px-5 py-2.5 text-[10px] font-semibold text-foreground-400 uppercase tracking-wider">Archivo</th>
                          <th className="text-left px-5 py-2.5 text-[10px] font-semibold text-foreground-400 uppercase tracking-wider hidden sm:table-cell">Tipo</th>
                          <th className="text-left px-5 py-2.5 text-[10px] font-semibold text-foreground-400 uppercase tracking-wider hidden md:table-cell">Tamaño</th>
                          <th className="text-left px-5 py-2.5 text-[10px] font-semibold text-foreground-400 uppercase tracking-wider hidden lg:table-cell">Fecha</th>
                          <th className="text-right px-5 py-2.5 text-[10px] font-semibold text-foreground-400 uppercase tracking-wider">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredFiles.map((file) => {
                          const blocked = isFree && file.isPesado;
                          return (
                            <tr key={file.id} className={`border-b border-background-100 last:border-0 hover:bg-background-100/50 transition-colors ${blocked ? 'opacity-50 bg-red-50/30' : ''}`}>
                              {/* Name + icon */}
                              <td className="px-5 py-3">
                                <div className="flex items-center gap-3">
                                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${blocked ? 'bg-red-100' : 'bg-background-200'}`}>
                                    <i className={`${fileTypeIcons[file.type]} ${blocked ? 'text-red-400' : 'text-foreground-500'} text-sm`}></i>
                                  </div>
                                  <div className="min-w-0">
                                    <p className={`text-xs font-semibold truncate max-w-[200px] sm:max-w-xs ${blocked ? 'text-red-400 line-through' : 'text-foreground-800'}`}>
                                      {file.name}
                                    </p>
                                    <div className="flex items-center gap-2 sm:hidden mt-0.5">
                                      <span className={`px-1.5 py-0.5 rounded-md text-[9px] font-semibold ${fileTypeColors[file.type]}`}>
                                        {fileTypeLabels[file.type]}
                                      </span>
                                      <span className="text-foreground-400 text-[9px]">{file.sizeDisplay}</span>
                                    </div>
                                    {blocked && (
                                      <span className="inline-flex items-center gap-1 text-red-500 text-[9px] font-semibold mt-0.5">
                                        <i className="ri-lock-line text-[8px]"></i>
                                        Bloqueado — requiere Pro/Premium
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </td>
                              {/* Type */}
                              <td className="px-5 py-3 hidden sm:table-cell">
                                <span className={`inline-flex px-2 py-1 rounded-md text-[10px] font-semibold ${fileTypeColors[file.type]}`}>
                                  {fileTypeLabels[file.type]}
                                </span>
                              </td>
                              {/* Size */}
                              <td className="px-5 py-3 hidden md:table-cell">
                                <div>
                                  <p className="text-xs font-medium text-foreground-800">{file.sizeDisplay}</p>
                                  {file.dimensions && <p className="text-[9px] text-foreground-400">{file.dimensions}</p>}
                                  {file.duration && <p className="text-[9px] text-foreground-400">{file.duration}</p>}
                                </div>
                              </td>
                              {/* Date */}
                              <td className="px-5 py-3 hidden lg:table-cell">
                                <p className="text-xs text-foreground-500">
                                  {new Date(file.uploadedAt).toLocaleDateString('es-GT', { day: 'numeric', month: 'short', year: 'numeric' })}
                                </p>
                              </td>
                              {/* Actions */}
                              <td className="px-5 py-3 text-right">
                                <div className="flex items-center justify-end gap-1">
                                  <button
                                    onClick={() => handleViewDetail(file)}
                                    className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"
                                    title="Ver detalle"
                                  >
                                    <i className="ri-eye-line text-foreground-500 text-xs"></i>
                                  </button>
                                  <button
                                    onClick={() => handleDelete(file)}
                                    className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-red-50 hover:text-red-500 transition-colors"
                                    title="Eliminar"
                                  >
                                    <i className="ri-delete-bin-line text-foreground-500 text-xs"></i>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Bottom actions */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <button
                  onClick={handleRequestStorage}
                  className="flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-add-circle-line text-sm"></i>
                  Solicitar más storage
                </button>
                <button
                  onClick={handleExportReport}
                  className="flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-background-50 border border-background-200 text-foreground-600 text-xs font-semibold hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-file-download-line text-sm"></i>
                  Exportar reporte
                </button>
              </div>
              </>
              )}
            </div>
          </div>
        </main>
      </div>

      {/* Detail modal */}
      {detailFile && (
        <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4" onClick={() => setDetailFile(null)}>
          <div
            className="bg-background-50 rounded-2xl border border-background-200 w-full max-w-md overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-background-200">
              <div className="flex items-center gap-3">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${detailFile.isPesado && isFree ? 'bg-red-100' : 'bg-background-200'}`}>
                  <i className={`${fileTypeIcons[detailFile.type]} ${detailFile.isPesado && isFree ? 'text-red-400' : 'text-foreground-500'} text-base`}></i>
                </div>
                <div>
                  <h3 className="text-foreground-950 font-bold text-sm">Detalle del Archivo</h3>
                  <p className="text-foreground-400 text-[10px]">{detailFile.name}</p>
                </div>
              </div>
              <button
                onClick={() => setDetailFile(null)}
                className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"
              >
                <i className="ri-close-line text-foreground-500"></i>
              </button>
            </div>

            {/* Detail rows */}
            <div className="p-5 space-y-3">
              <div className="flex items-center justify-between py-2 border-b border-background-100">
                <span className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Nombre</span>
                <span className="text-foreground-800 text-xs font-medium text-right max-w-[250px] truncate">{detailFile.name}</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-background-100">
                <span className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Tipo</span>
                <span className={`inline-flex px-2 py-0.5 rounded-md text-[10px] font-semibold ${fileTypeColors[detailFile.type]}`}>
                  {fileTypeLabels[detailFile.type]}
                </span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-background-100">
                <span className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Tamaño</span>
                <span className="text-foreground-800 text-xs font-medium">{detailFile.sizeDisplay}</span>
              </div>
              {detailFile.dimensions && (
                <div className="flex items-center justify-between py-2 border-b border-background-100">
                  <span className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Dimensiones</span>
                  <span className="text-foreground-800 text-xs font-medium">{detailFile.dimensions}</span>
                </div>
              )}
              {detailFile.duration && (
                <div className="flex items-center justify-between py-2 border-b border-background-100">
                  <span className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Duración</span>
                  <span className="text-foreground-800 text-xs font-medium">{detailFile.duration}</span>
                </div>
              )}
              <div className="flex items-center justify-between py-2 border-b border-background-100">
                <span className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Fecha de subida</span>
                <span className="text-foreground-800 text-xs font-medium">
                  {new Date(detailFile.uploadedAt).toLocaleDateString('es-GT', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-background-100">
                <span className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Pesado</span>
                <span className={`text-xs font-semibold ${detailFile.isPesado ? 'text-red-500' : 'text-emerald-500'}`}>
                  {detailFile.isPesado ? 'Sí — requiere Pro/Premium' : 'No — compatible con Free'}
                </span>
              </div>
              {detailFile.isPesado && isFree && (
                <div className="p-3 rounded-xl bg-red-50 border border-red-200 mt-1">
                  <p className="text-red-700 text-[10px] font-semibold flex items-center gap-1.5">
                    <i className="ri-lock-line text-xs"></i>
                    Este archivo está bloqueado en tu plan Free
                  </p>
                  <p className="text-red-500 text-[9px] mt-1">Actualizá a Pro o Premium para acceder a este tipo de archivos.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}


    </div>
  );
}